import { createContext, useContext } from 'react';

import type { IDataCheckGroup } from '../../interfaces/checks';
import type {
  ICreateProspectContact,
  ICreateProspectInput,
  ICreateProspectResponse,
  IProspect,
  IUpdateProspectContact,
  IUpdateProspectInput
} from '../../interfaces/prospects';
import type { ProspectStatus } from '../../types/prospects';

export interface IProspectsContextValue {
  prospects: IProspect[];
  filtered: IProspect[];
  loading: boolean;
  hasAnyProspect: boolean | null;
  statusFilter: ProspectStatus | null;
  setStatusFilter: (status: ProspectStatus | null) => void;
  query: string;
  setQuery: (query: string) => void;
  isSearching: boolean;
  selectedId: number | null;
  setSelectedId: (id: number | null) => void;
  selected: IProspect | null;
  selectedActivationGroup: IDataCheckGroup | null;
  getProspectById: (id: number) => IProspect | undefined;
  refresh: () => void;
  createProspect: (body: ICreateProspectInput) => Promise<ICreateProspectResponse>;
  updateProspect: (id: number, input: IUpdateProspectInput) => Promise<void>;
  createContact: (id: number, input: ICreateProspectContact) => Promise<ICreateProspectResponse>;
  updateContact: (id: number, contactId: number, input: IUpdateProspectContact) => Promise<void>;
  requestSelectAfterRefresh: (id: number) => void;
  setNewProspectCreatedId: (id: number | null) => void;
  newProspectCreatedId: number | null;
}

export const ProspectsContext = createContext<IProspectsContextValue | null>(null);

export const useProspects = (): IProspectsContextValue => {
  const context = useContext(ProspectsContext);
  if (!context) {
    throw new Error('useProspects must be used within a ProspectsProvider');
  }
  return context;
};
